/**
 * The payoff: who won, how much, and with what — in words.
 *
 * The engine's `pot_push` says who and how much; it does not name the hand, and the
 * Director's timeline is not edited for looks (Phase 3 review), so the scene works the
 * result out for itself from what is on the felt at the payoff moment: the shown hands
 * and the five-card board. The ranking is the engine's own `evaluate7` — already in the
 * web bundle through `lib/table-game.ts`, so importing it here adds nothing — and the
 * name is decoded from the score it packs (category, then kickers, base 16).
 *
 * Two cases:
 *   - a showdown: two or more seats have their cards up and the board is complete, so
 *     every shown hand is scored and the best wins (ties split);
 *   - an uncontested win: one seat is left standing, nobody showed anything, and the
 *     line is "takes it" — there is no hand to name.
 *
 * Pure functions, no DOM, so they can be tested under Node.
 */
import type { TableState } from "@engine/table-state";
import { evaluate7, RANKS } from "@pipeline/solve/evaluator";

/** The categories `evaluate7` packs, in its own order. */
const CATEGORY = ["high card", "a pair", "two pair", "three of a kind", "a straight", "a flush", "a full house", "four of a kind", "a straight flush"] as const;

const RANK_WORD: Record<string, string> = {
  "2": "two", "3": "three", "4": "four", "5": "five", "6": "six", "7": "seven", "8": "eight",
  "9": "nine", "T": "ten", "J": "jack", "Q": "queen", "K": "king", "A": "ace",
};
const plural = (w: string) => (w === "six" ? "sixes" : `${w}s`);
const rankWord = (r: number) => RANK_WORD[RANKS[r]!] ?? "";

/** Decode the packed score: category and the kickers, high to low. */
export function decodeScore(score: number): { category: number; ranks: number[] } {
  let v = score;
  const ks: number[] = [];
  for (let i = 0; i < 5; i++) { ks.unshift((v % 16) - 1); v = Math.floor(v / 16); }
  return { category: v, ranks: ks };
}

/**
 * The hand, named the way a dealer would call it. "three eights", "a pair of aces",
 * "two pair, aces and eights", "a full house, eights over aces", "a straight, nine
 * high", "an ace-high flush", "four nines", "a straight flush", "a royal flush".
 */
export function handName(score: number): string {
  const { category, ranks } = decodeScore(score);
  const [a, b] = ranks;
  switch (category) {
    case 0: return `${rankWord(a!)} high`;
    case 1: return `a pair of ${plural(rankWord(a!))}`;
    case 2: return `two pair, ${plural(rankWord(a!))} and ${plural(rankWord(b!))}`;
    case 3: return `three ${plural(rankWord(a!))}`;
    case 4: return `a straight, ${rankWord(a!)} high`;
    case 5: return `${a === 12 ? "an" : "a"} ${rankWord(a!)}-high flush`;
    case 6: return `a full house, ${plural(rankWord(a!))} over ${plural(rankWord(b!))}`;
    case 7: return `four ${plural(rankWord(a!))}`;
    case 8: return a === 12 ? "a royal flush" : `a straight flush, ${rankWord(a!)} high`;
    default: return CATEGORY[category] ?? "";
  }
}

export interface Payoff {
  /** Winning seats (more than one on a split). */
  winners: number[];
  /** Everyone whose cards are shown and who did not win. */
  losers: number[];
  /** The pot on the felt at the payoff moment. */
  amount: number;
  /** Named on a showdown; null on an uncontested win. */
  hand: string | null;
  uncontested: boolean;
}

/**
 * The result, read off the presented table.
 *
 * Returns null when the felt is not at a payoff moment: a showdown needs a full board
 * and at least two hands up; an uncontested win needs exactly one seat not folded and
 * nothing shown.
 */
export function derivePayoff(state: TableState): Payoff | null {
  const live = state.seats.filter((s) => !s.folded && !s.sittingOut && (s.stack > 0 || s.allIn || s.committed > 0));
  const shown = live.filter((s) => s.hole && s.hole.length === 2);
  if (shown.length >= 2 && state.board.length === 5) {
    let best = -Infinity;
    const scores = new Map<number, number>();
    for (const s of shown) {
      const sc = evaluate7([...s.hole!, ...state.board]);
      scores.set(s.index, sc);
      if (sc > best) best = sc;
    }
    const winners = shown.filter((s) => scores.get(s.index) === best).map((s) => s.index);
    const losers = shown.filter((s) => scores.get(s.index) !== best).map((s) => s.index);
    return { winners, losers, amount: state.pot, hand: handName(best), uncontested: false };
  }
  /*
   * PHASE 7 (found by box 3's seeded-hand proof): the VIEWER's own cards do not count
   * as shown. The presented state always carries the hero's hole cards — they are the
   * viewer's — so "one seat left and nobody showed" was never true when that seat was
   * the hero, and a pot the player won uncontested had no payoff: no "You win" line,
   * no ring pulse, and no Show offer, which Phase 6 decided the player gets after an
   * uncontested win. A bot's cards being up still means a showdown, as before.
   */
  const shownByOthers = shown.filter((s) => !s.isSelf);
  if (live.length === 1 && shownByOthers.length === 0 && state.pot > 0) {
    return { winners: [live[0]!.index], losers: [], amount: state.pot, hand: null, uncontested: true };
  }
  return null;
}

/**
 * The band's line. `name(seat)` gives "You" for the hero and the short name otherwise;
 * `fmt` is the table's money format.
 */
export function payoffLine(p: Payoff, name: (seat: number) => string, fmt: (v: number) => string): string {
  const isYou = (seat: number) => name(seat) === "You";
  if (p.winners.length > 1) {
    const each = fmt(p.amount / p.winners.length);
    return `Split pot — ${p.winners.map(name).join(" and ")}, ${each} each`;
  }
  const w = p.winners[0]!;
  if (p.uncontested) return `${name(w)} ${isYou(w) ? "take" : "takes"} it — ${fmt(p.amount)}`;
  return `${name(w)} ${isYou(w) ? "win" : "wins"} ${fmt(p.amount)} with ${p.hand}`;
}
